import { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { LEGAL_LOCATION, LEGAL_NAME } from "../config/branding";

export default function Contact() {
  const formRef = useRef(null);
  const routerLocation = useLocation();
  const [loading, setLoading] = useState(false);
  const [whatsAppLink, setWhatsAppLink] = useState("");
  const [errors, setErrors] = useState({});
  const [captcha, setCaptcha] = useState(() => createCaptcha());
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });

    setTimeout(() => {
      setNotification({ show: false, type: "", message: "" });
    }, 4000);
  };

  const clearError = (field) => {
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const resetCaptcha = () => {
    setCaptcha(createCaptcha());
    setCaptchaAnswer("");
  };

  useEffect(() => {
    if (formRef.current) {
      const form = formRef.current;
      const eventInterestField = form.elements.namedItem("event_interest");
      const serviceField = form.elements.namedItem("service");
      const messageField = form.elements.namedItem("message");

      if (routerLocation.state?.eventMessage && messageField) {
        messageField.value = routerLocation.state.eventMessage;
      }

      if (routerLocation.state?.productMessage && messageField) {
        messageField.value = routerLocation.state.productMessage;
      }

      if (eventInterestField) {
        eventInterestField.value = routerLocation.state?.eventInterest || "";
      }

      if (routerLocation.state?.serviceValue && serviceField) {
        serviceField.value = routerLocation.state.serviceValue;
      }

      if (routerLocation.state?.eventMessage || routerLocation.state?.productMessage) {
        const contactSection = document.getElementById("contact");
        if (contactSection) {
          setTimeout(() => {
            contactSection.scrollIntoView({ behavior: "smooth" });
          }, 200);
        }
      }
    }
  }, [routerLocation]);

  const sendEmail = async (e) => {
    e.preventDefault();
    if (loading) return;

    const form = formRef.current;
    const name = form.from_name.value.trim();
    const email = form.from_email.value.trim();
    const nextErrors = {};

    if (!/^[\p{L}][\p{L}\s'-]*$/u.test(name)) {
      nextErrors.name = "Enter a valid name using letters only.";
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (Number(captchaAnswer) !== captcha.answer) {
      nextErrors.captcha = "Please answer the human check correctly.";
    }

    if (form.website.value) {
      // This hidden field is only filled by automated bots.
      showNotification("success", "Message sent successfully. Our team representative will get back to you shortly.");
      return;
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setLoading(true);

    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      const sheetResponse = await fetch(
        "https://api.sheety.co/48b73cf8190f6c260c69e64c0234dd60/jisedAiLeads/sheet1",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sheet1: {
              name: formRef.current.from_name.value,
              email: formRef.current.from_email.value,
              service: formRef.current.service.value,
              event: formRef.current.event_interest?.value || "",
              message: formRef.current.message.value,
              date: new Date().toISOString(),
            },
          }),
        }
      );

      const sheetData = await sheetResponse.json();
      console.log("Sheety status:", sheetResponse.status);
      console.log("Sheety response:", sheetData);

      if (!sheetResponse.ok) {
        throw new Error(
          sheetData?.errors?.[0]?.detail || "Failed to save lead"
        );
      }

      const phone = "254777748910";
      const text = `New lead from website:
Name: ${formRef.current.from_name.value}
Service: ${formRef.current.service.value}
Event: ${formRef.current.event_interest?.value || "None"}`;

      setWhatsAppLink(
        `https://wa.me/${phone}?text=${encodeURIComponent(text)}`
      );

      showNotification(
        "success",
        "Message sent successfully. Our team representative will get back to you shortly."
      );

      form.reset();
      resetCaptcha();
      setErrors({});
    } catch (error) {
      console.error(error);
      showNotification(
        "error",
        error.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-[#eeeee8] px-5 py-24 text-[#111712] overflow-hidden sm:px-8 md:px-12 md:py-32"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-16 left-1/4 h-56 w-56 rounded-full bg-[#bfd4c4]/45 blur-3xl" />
        <div className="absolute bottom-10 right-1/4 h-64 w-64 rounded-full bg-[#d6e85c]/30 blur-3xl" />
      </div>

      {notification.show && (
        <div
          className={`fixed top-24 right-6 z-[9999] px-5 py-3 rounded-lg shadow-lg border ${
            notification.type === "success"
              ? "bg-green-600/90 border-green-400 text-white"
              : "bg-red-600/90 border-red-400 text-white"
          }`}
        >
          {notification.message}
        </div>
      )}

      <div className="relative max-w-7xl mx-auto">
        <h2 className="mb-14 text-center font-serif text-5xl tracking-[-0.05em] text-[#111712] md:text-6xl">
          Contact Us
        </h2>

        <div className="grid md:grid-cols-2 gap-14 items-start">
          <div>
            <h3 className="mb-6 font-serif text-4xl tracking-[-0.04em] text-[#111712]">
              Let's Connect
            </h3>

            <p className="mb-10 max-w-xl text-lg leading-8 text-[#4d5951]">
              {LEGAL_NAME} is here to help. We'll make sure your message reaches
              the right people.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <Mail className="mt-1 text-[#45695a]" size={26} />
                <div>
                  <h4 className="font-semibold text-xl">Email</h4>
                  <a
                    href="mailto:info@jisedai.africa"
                    className="text-[#4d5951] transition hover:text-[#45695a]"
                  >
                    info[at]jisedai.africa
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="mt-1 text-[#45695a]" size={26} />
                <div>
                  <h4 className="font-semibold text-xl">Phone</h4>
                  <a
                    href="tel:+254777748910"
                    className="text-[#4d5951] transition hover:text-[#45695a]"
                  >
                    +254777 74 8910
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <MapPin className="mt-1 text-[#45695a]" size={26} />
                <div>
                  <h4 className="font-semibold text-xl">Location</h4>
                  <p className="text-[#4d5951]">{LEGAL_LOCATION}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="mt-1 text-[#45695a]" size={26} />
                <div>
                  <h4 className="font-semibold text-xl">Office Hours</h4>
                  <p className="text-[#4d5951]">
                    Mon - Fri: 9:00 AM - 5:00 PM (EAT)
                  </p>
                </div>
              </div>
            </div>
          </div>

          <form
            ref={formRef}
            onSubmit={sendEmail}
            className="rounded-3xl border border-[#111712]/15 bg-white/65 p-8 shadow-xl shadow-[#45695a]/10 md:p-10"
          >
            <h3 className="mb-3 font-serif text-4xl tracking-[-0.04em] text-[#111712]">
              Send a Message
            </h3>

            <p className="mb-8 text-[#4d5951]">
              Share what you need and we will guide you to the right service,
              event, or collaboration path.
            </p>

            <input
              type="hidden"
              name="event_interest"
              defaultValue={routerLocation.state?.eventInterest || ""}
            />

            <div className="absolute -left-[10000px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" type="text" tabIndex="-1" autoComplete="off" />
            </div>

            <div className="space-y-5">
              <div>
                <input
                  type="text"
                  name="from_name"
                  placeholder="Full Name"
                  autoComplete="name"
                  required
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? "name-error" : undefined}
                  onInput={(event) => {
                    event.currentTarget.value = event.currentTarget.value.replace(/[^\p{L}\s'-]/gu, "");
                    clearError("name");
                  }}
                  className={`w-full rounded-xl border bg-white px-4 py-4 text-[#111712] placeholder:text-[#647169] focus:outline-none focus:border-[#45695a] focus:shadow-[0_0_18px_rgba(69,105,90,0.16)] transition ${errors.name ? "border-red-400" : "border-[#111712]/15"}`}
                />
                {errors.name && <p id="name-error" className="mt-2 text-sm text-red-300">{errors.name}</p>}
              </div>

              <div>
                <input
                  type="email"
                  name="from_email"
                  placeholder="Email Address"
                  autoComplete="email"
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  onInput={() => clearError("email")}
                  className={`w-full rounded-xl border bg-white px-4 py-4 text-[#111712] placeholder:text-[#647169] focus:outline-none focus:border-[#45695a] focus:shadow-[0_0_18px_rgba(69,105,90,0.16)] transition ${errors.email ? "border-red-400" : "border-[#111712]/15"}`}
                />
                {errors.email && <p id="email-error" className="mt-2 text-sm text-red-300">{errors.email}</p>}
              </div>

              <select
                name="service"
                required
                defaultValue=""
                className="w-full rounded-xl border border-[#111712]/15 bg-white px-4 py-4 text-[#111712] focus:outline-none focus:border-[#45695a] focus:shadow-[0_0_18px_rgba(69,105,90,0.16)] transition"
              >
                <option value="" disabled>
                  --Select Service--
                </option>
                <option value="AI Consulting">AI Consulting</option>
                <option value="Machine Learning">Machine Learning</option>
                <option value="AI Education">AI Education</option>
                <option value="Training">Training</option>
                <option value="Data Intelligence">Data Intelligence</option>
              </select>

              <textarea
                name="message"
                rows="6"
                placeholder="Your Message"
                required
                className="w-full resize-none rounded-xl border border-[#111712]/15 bg-white px-4 py-4 text-[#111712] placeholder:text-[#647169] focus:outline-none focus:border-[#45695a] focus:shadow-[0_0_18px_rgba(69,105,90,0.16)] transition"
              />

              <div>
                <label htmlFor="human-check" className="mb-2 block text-sm font-medium text-[#344039]">
                  Human check: What is {captcha.first} + {captcha.second}?
                </label>
                <div className="flex gap-3">
                  <input
                    id="human-check"
                    type="number"
                    inputMode="numeric"
                    value={captchaAnswer}
                    onChange={(event) => {
                      setCaptchaAnswer(event.target.value);
                      clearError("captcha");
                    }}
                    required
                    aria-invalid={Boolean(errors.captcha)}
                    aria-describedby={errors.captcha ? "captcha-error" : undefined}
                    className={`min-w-0 flex-1 rounded-xl border bg-white px-4 py-4 text-[#111712] placeholder:text-[#647169] focus:outline-none focus:border-[#45695a] focus:shadow-[0_0_18px_rgba(69,105,90,0.16)] transition ${errors.captcha ? "border-red-400" : "border-[#111712]/15"}`}
                    placeholder="Your answer"
                  />
                  <button
                    type="button"
                    onClick={resetCaptcha}
                    className="rounded-xl border border-[#111712]/15 px-4 text-sm font-medium text-[#344039] transition hover:border-[#45695a] hover:text-[#45695a]"
                  >
                    New question
                  </button>
                </div>
                {errors.captcha && <p id="captcha-error" className="mt-2 text-sm text-red-300">{errors.captcha}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#d6e85c] py-4 font-semibold text-[#111712] transition hover:bg-[#cce141] hover:shadow-[0_0_20px_rgba(214,232,92,0.35)] disabled:opacity-70"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>

              {whatsAppLink && (
                <a
                  href={whatsAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full rounded-xl border border-[#45695a]/40 py-4 text-center font-semibold text-[#45695a] transition hover:bg-[#45695a]/10"
                >
                  Continue on WhatsApp
                </a>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function createCaptcha() {
  const first = Math.floor(Math.random() * 8) + 2;
  const second = Math.floor(Math.random() * 8) + 1;

  return { first, second, answer: first + second };
}
